import { apiClient } from "./apiClient";


function snakeToCamel<T>(obj: Record<string, unknown>): T {

  const result: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(obj)) {

    const camelKey = key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

    result[camelKey] = val;

  }

  return result as T;

}


const BASE_PATH = "/admin/lightgbm";


export interface LightGBMOverview {

  enabled: boolean;

  threshold: number;

  mode: string;

  modelVersion: string | null;

  trainedAt: string | null;

  decisions24h: number;

  passRate24h: number | null;

  totalDecisions: number;

  totalSamples: number;

  gateHealthy: boolean;

}


export interface LightGBMDecisionItem {

  id: string;

  symbol: string;

  score: number | null;

  threshold: number | null;

  passed: boolean | null;

  reason: string | null;

  stage: string | null;

  modelRole: string | null;

  modelVersion: string | null;

  createdAt: string | null;

}


export interface LightGBMDecisionsResponse {

  items: LightGBMDecisionItem[];

  total: number;

  page: number;

  pageSize: number;

  totalPages: number;

}


export interface TrainingRun {

  id: string;

  status: string;

  startedAt: string | null;

  completedAt: string | null;

  sampleCount: number | null;

  candidateVersion: string | null;

  trainingMode: string | null;

  errorMessage: string | null;

  notes: string | null;

}


export interface TrainingRunList {

  runs: TrainingRun[];

  count: number;

}


export interface TrainingLogs {

  lines: string[];

  totalLines: number;

  eof: boolean;

  status: string;

}


export interface ActionResult {

  success: boolean;

  message: string;

  id?: string | null;

}


export async function fetchOverview(): Promise<LightGBMOverview> {

  const resp = await apiClient.get<Record<string, unknown>>(

    `${BASE_PATH}/overview`,

  );

  return snakeToCamel<LightGBMOverview>(resp.data);

}


export async function fetchDecisions(params: {

  page?: number;

  pageSize?: number;

  symbol?: string;

  sortBy?: string;

  sortOrder?: string;

  dateFrom?: string;

  dateTo?: string;

}): Promise<LightGBMDecisionsResponse> {

  const resp = await apiClient.get<Record<string, unknown>>(

    `${BASE_PATH}/decisions`,

    { params },

  );

  const raw = resp.data as Record<string, unknown>;

  return {

    items: ((raw.items as Record<string, unknown>[]) || []).map((item) => snakeToCamel<LightGBMDecisionItem>(item)),

    total: raw.total as number,

    page: raw.page as number,

    pageSize: raw.page_size as number,

    totalPages: raw.total_pages as number,

  };

}


export async function getTrainingRuns(): Promise<TrainingRunList> {

  const { data } = await apiClient.get<TrainingRunList>(

    `${BASE_PATH}/training-runs`,

  );

  return data;

}


export async function triggerTraining(): Promise<ActionResult> {

  const { data } = await apiClient.post<ActionResult>(

    `${BASE_PATH}/training-runs`,

  );

  return data;

}


export async function getTrainingLogs(

  runId: string,

  afterLine = 0,

): Promise<TrainingLogs> {

  const { data } = await apiClient.get<TrainingLogs>(

    `${BASE_PATH}/training-runs/${runId}/logs`,

    { params: { after_line: afterLine } },

  );

  return data;

}


export async function deleteTrainingRun(runId: string): Promise<ActionResult> {

  const { data } = await apiClient.delete<ActionResult>(

    `${BASE_PATH}/training-runs/${runId}`,

  );

  return data;

}

